import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { createPageUrl } from "@/utils";
import { getDefaultCoverImage } from "@/utils/defaultCoverImage";

export default function MyReviews() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      
      if (!session) {
        navigate(createPageUrl("Auth"));
        return;
      }
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
      
      if (error) {
        console.error("Error loading profile:", error);
        navigate(createPageUrl("Auth"));
        return;
      }
      
      setCurrentUser(profile);
    } catch (error) {
      console.error("Error loading user:", error);
      navigate(createPageUrl("Auth"));
    }
  };

  const { data: reviews, isLoading } = useQuery({
    queryKey: ["my-reviews", currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return [];
      
      const { data, error } = await supabase
        .from("feedback")
        .select(`
          *,
          track:tracks(title, artist_name, cover_image_url, artist_id)
        `)
        .eq("reviewer_id", currentUser.id)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentUser?.id,
  });

  if (isLoading || !currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-foreground">My Reviews</h1>
          <Button onClick={() => navigate("/Discover")} tooltip="Go to discover page to review more tracks">Review More Tracks</Button>
        </div>

        {!reviews || reviews.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">You haven't left any reviews yet.</p>
            <Button onClick={() => navigate("/Discover")} tooltip="Start reviewing tracks to earn credits">Start Reviewing</Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {reviews.map((review) => (
              <div 
                key={review.id} 
                className="bg-card p-6 rounded-lg border border-border hover:border-primary transition-colors cursor-pointer"
                onClick={() => navigate(`/ViewFeedback?id=${review.id}`)}
              >
                <div className="flex items-start gap-4">
                  <img
                    src={review.track?.cover_image_url || getDefaultCoverImage(review.track?.id)}
                    alt={review.track?.title}
                    className="w-20 h-20 rounded object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-semibold text-card-foreground">
                          {review.track?.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          by {review.track?.artist_name}
                        </p>
                      </div>
                      <Badge variant={
                        review.feedback_type === 'praise' ? 'default' :
                        review.feedback_type === 'scathe' ? 'destructive' :
                        'secondary'
                      }>
                        {review.feedback_type}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-3">
                      {new Date(review.created_at).toLocaleDateString()} at {new Date(review.created_at).toLocaleTimeString()}
                    </p>

                    {review.comment && (
                      <p className="text-card-foreground line-clamp-2">{review.comment}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {reviews && reviews.length > 0 && (
          <div className="mt-8 p-6 bg-muted rounded-lg">
            <h2 className="text-xl font-bold text-foreground mb-2">Review Statistics</h2>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-foreground">{reviews.length}</div>
                <div className="text-sm text-muted-foreground">Total Reviews</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-foreground">
                  {reviews.filter(r => r.feedback_type === 'praise').length}
                </div>
                <div className="text-sm text-muted-foreground">Praise</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-foreground">
                  {reviews.filter(r => r.feedback_type === 'scathe').length}
                </div>
                <div className="text-sm text-muted-foreground">Scathe</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}